import { DestroyRef, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, Subject, tap } from 'rxjs';

import { NotificationsStore } from '../state/notifications.store';
import { NotificationsQuery } from '../state/notifications.query';
import { NotificationsPusherService } from './notifications-pusher.service';
import { ApiClientNotificationsService } from 'src/app/api/api-client-notifications.service';
import { GetNotificationsParams, NotificationInboxDto, NotificationTypeEnum } from '../models/notification.interface';
import { LoggerService } from 'src/app/logger/logger.service';
import { NotificationAssessmentIntentService } from './notification-assessment-intent.service';

@Injectable()
export class NotificationsService {

    private readonly loggerName = 'NotificationsService';

    readonly newNotification$ = new Subject<NotificationInboxDto>();

    constructor(
        private store: NotificationsStore,
        private query: NotificationsQuery,
        private pusherService: NotificationsPusherService,
        private apiClient: ApiClientNotificationsService,
        private logger: LoggerService,
        private destroyRef: DestroyRef,
        private ngZone: NgZone,
        private notificationAssessmentIntent: NotificationAssessmentIntentService,
    ) {}

    init(): void {
        this.fetchNotifications();
        this.initPusher();
    }

    refresh(): void {
        this.store.set({ items: [], unreadCount: 0, hasMore: false, nextCursor: null });
        this.fetchNotifications();
    }

    private fetchNotifications(): void {
        const filterUnread = this.store.get('filterUnread');
        this.store.set({ loading: true });
        const params: GetNotificationsParams = filterUnread ? { IsRead: false } : {};
        this.apiClient.getNotifications(params).pipe(
            tap(response => {
                const unreadCount = response.items.filter(item => !item.isRead).length;
                this.store.set({
                    items: response.items,
                    unreadCount,
                    hasMore: !!response.nextPage,
                    nextCursor: response.nextPage,
                    loading: false,
                });
            }),
            catchError(err => {
                this.logger.error(this.loggerName, 'Error fetching notifications', err);
                this.store.set({ loading: false });
                return EMPTY;
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    loadMore(): void {
        const cursor = this.query.nextCursor;
        if (!cursor) {
            return;
        }
        const filterUnread = this.store.get('filterUnread');
        this.store.set({ loadingMore: true });
        const params: GetNotificationsParams = { NextPage: cursor, ...(filterUnread ? { IsRead: false } : {}) };
        this.apiClient.getNotifications(params).pipe(
            tap(response => {
                const existing = this.store.get('items');
                const newItems = response.items.filter(item => !existing.some(e => e.id === item.id));
                const allItems = [...existing, ...newItems];
                const unreadCount = allItems.filter(item => !item.isRead).length;
                this.store.set({
                    items: allItems,
                    unreadCount,
                    hasMore: !!response.nextPage,
                    nextCursor: response.nextPage,
                    loadingMore: false,
                });
            }),
            catchError(err => {
                this.logger.error(this.loggerName, `Error loading more notifications: ${err}`);
                this.store.set({ loadingMore: false });
                return EMPTY;
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    private initPusher(): void {
        this.pusherService.init();
        this.pusherService.notificationReceived.pipe(
            tap(notification =>
                this.ngZone.run(() => this.onNewNotification(notification)),
            ),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    private onNewNotification(notification: NotificationInboxDto): void {
        const existing = this.store.get('items');
        const index = existing.findIndex(item => item.id === notification.id);
        if (index === -1) {
            const unreadDelta = notification.isRead ? 0 : 1;
            this.store.set({
                items: [notification, ...existing],
                unreadCount: this.store.get('unreadCount') + unreadDelta,
            });
            this.newNotification$.next(notification);
            this.emitAssessmentNoteIntentIfNeeded(notification);
            this.emitAiTemplateReadyIfNeeded(notification);
            return;
        }
        const merged = this.mergeInboxNotification(existing[index], notification);
        const items = [...existing];
        items[index] = merged;
        const unreadCount = items.filter(item => !item.isRead).length;
        this.store.set({ items, unreadCount });
        this.newNotification$.next(merged);
        this.emitAssessmentNoteIntentIfNeeded(merged);
        // NOTE: AiTemplateReady is emitted only on the new-item path above — re-emitting on a Pusher
        // redelivery/merge would trigger a redundant gallery refetch + list flicker.
    }

    /** "Create a template with AI" finished → tell the templates gallery to refetch so the new
     *  template shows (and can be opened from the bell) without a manual page refresh. */
    private emitAiTemplateReadyIfNeeded(notification: NotificationInboxDto): void {
        if (notification.notificationType === NotificationTypeEnum.AiTemplateReady) {
            this.notificationAssessmentIntent.emitAiTemplateReady();
        }
    }

    /**
     * Pusher / partial payloads can omit `metadata`; shallow merge would drop `assessmentId` and break open-tab refresh.
     */
    private mergeInboxNotification(existing: NotificationInboxDto, incoming: NotificationInboxDto): NotificationInboxDto {
        return {
            ...existing,
            ...incoming,
            metadata: {
                ...(existing.metadata ?? {}),
                ...(incoming.metadata ?? {}),
            },
        };
    }

    /**
     * Lets the assessment page refresh notes / deep-link when a note notification arrives while that assessment is already open.
     */
    private emitAssessmentNoteIntentIfNeeded(notification: NotificationInboxDto): void {
        if (notification.notificationType !== NotificationTypeEnum.AssessmentNoteAdded) {
            return;
        }
        const assessmentId = notification.metadata?.assessmentId ?? notification.entityId;
        if (!assessmentId) {
            return;
        }
        this.notificationAssessmentIntent.emitOpenAssessmentFromNotification({
            assessmentId,
            sectionId: notification.metadata?.sectionId,
            assessmentQuestionId: notification.metadata?.assessmentQuestionId,
            notesThreadId: notification.metadata?.notesThreadId,
        });
    }

    openPanel(): void {
        this.store.set({ panelOpen: true });
    }

    closePanel(): void {
        this.store.set({ panelOpen: false });
    }

    toggleFilter(): void {
        this.store.set({ filterUnread: !this.store.get('filterUnread') });
        this.refresh();
    }

    markAsRead(id: string): void {
        this.apiClient.patchNotification(id, [{ op: 'replace', path: '/isRead', value: true }]).pipe(
            tap(() => this.updateItemReadState(id, true)),
            catchError(err => {
                this.logger.error(this.loggerName, `Error marking notification ${id} as read: ${err}`);
                return EMPTY;
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    markAsUnread(id: string): void {
        this.apiClient.patchNotification(id, [{ op: 'replace', path: '/isRead', value: false }]).pipe(
            tap(() => this.updateItemReadState(id, false)),
            catchError(err => {
                this.logger.error(this.loggerName, `Error marking notification ${id} as unread: ${err}`);
                return EMPTY;
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    private updateItemReadState(id: string, isRead: boolean): void {
        let items = this.store.get('items').map(item =>
            item.id === id ? { ...item, isRead } : item
        );
        if (this.store.get('filterUnread') && isRead) {
            items = items.filter(item => item.id !== id);
        }
        const unreadCount = items.filter(item => !item.isRead).length;
        this.store.set({ items, unreadCount });
    }

    markAllAsRead(): void {
        this.store.get('items')
            .filter(item => !item.isRead)
            .forEach(item => this.markAsRead(item.id));
    }

    deleteNotification(id: string): void {
        this.apiClient.deleteNotification(id).pipe(
            tap(() => {
                const items = this.store.get('items').filter(item => item.id !== id);
                const unreadCount = items.filter(item => !item.isRead).length;
                this.store.set({ items, unreadCount });
            }),
            catchError(err => {
                this.logger.error(this.loggerName, `Error deleting notification ${id}: ${err}`);
                return EMPTY;
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    onNotificationClick(notification: NotificationInboxDto): void {
        if (!notification.isRead) {
            this.markAsRead(notification.id);
        }
        this.closePanel();
    }
}
